import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { getHowToSchema, getBreadcrumbSchema } from "@/lib/schema";
import { 
  ArrowRight, 
  CheckCircle2, 
  Target, 
  Users, 
  Zap,
  LineChart,
  AlertTriangle,
  Lightbulb,
  DollarSign,
  Clock,
  Rocket,
  Heart
} from "lucide-react";

const PreLaunchGuide = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Pre-Launch Guide - How to Launch Your Startup Successfully"
        description="Learn how to successfully launch your startup with our comprehensive pre-launch guide. Discover strategies, tips, and best practices for idea validation and market entry."
        keywords="pre-launch guide, startup launch, idea validation, market entry, startup strategy, launch checklist"
        canonicalUrl="/pre-launch-guide"
        jsonLd={[
          getBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Pre-Launch Guide", url: "/pre-launch-guide" },
          ]),
          getHowToSchema(
            "How to Launch Your Startup Successfully",
            "A comprehensive guide to validating and launching your startup.",
            [
              { name: "Validate Your Idea", text: "Test your idea with real users before building to ensure market demand." },
              { name: "Build an Audience", text: "Start building your email list and community before launch." },
              { name: "Create a Landing Page", text: "Set up a compelling landing page to capture interest and signups." },
              { name: "Launch and Iterate", text: "Launch your MVP, gather feedback, and continuously improve." },
            ]
          ),
        ]}
      />

      {/* Hero Section */}
      <div className="relative px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 opacity-90"></div>
        </div>
        <div className="relative text-center">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
            The Ultimate Pre-Launch Guide
          </h1>
          <p className="max-w-2xl mx-auto mt-6 text-xl text-gray-600">
            A comprehensive roadmap to validate and launch your startup successfully
          </p>
          <div className="flex justify-center mt-8 gap-4">
            <Button className="inline-flex items-center px-6 py-3 text-lg" variant="default" asChild>
              <a href="/signup">
                Start Validating <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
            <Button className="inline-flex items-center px-6 py-3 text-lg" variant="outline" asChild>
              <a href="#guide-content">
                Read Guide <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-y border-gray-200">
        <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="px-4 py-5 bg-gray-50 rounded-lg">
              <dt className="text-sm font-medium text-gray-500">Average Time to Validate</dt>
              <dd className="mt-1 text-3xl font-semibold text-indigo-600">2 Weeks</dd>
            </div>
            <div className="px-4 py-5 bg-gray-50 rounded-lg">
              <dt className="text-sm font-medium text-gray-500">Success Rate</dt>
              <dd className="mt-1 text-3xl font-semibold text-indigo-600">83%</dd>
            </div>
            <div className="px-4 py-5 bg-gray-50 rounded-lg">
              <dt className="text-sm font-medium text-gray-500">Cost Savings</dt>
              <dd className="mt-1 text-3xl font-semibold text-indigo-600">$15K+</dd>
            </div>
            <div className="px-4 py-5 bg-gray-50 rounded-lg">
              <dt className="text-sm font-medium text-gray-500">Ideas Validated</dt>
              <dd className="mt-1 text-3xl font-semibold text-indigo-600">1000+</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Main Content */}
      <div id="guide-content" className="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto space-y-16">
          {/* Section 1: Research & Planning */}
          <section>
            <div className="flex items-center gap-2">
              <Target className="w-8 h-8 text-indigo-600" />
              <h2 className="text-3xl font-bold text-gray-900">1. Research & Planning</h2>
            </div>
            <div className="mt-6 space-y-4">
              <div className="flex items-start">
                <CheckCircle2 className="flex-shrink-0 w-6 h-6 text-green-500" />
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Market Research</h3>
                  <p className="mt-2 text-gray-500">
                    Understand your target audience, competitors, and market opportunities. Our platform helps you:
                  </p>
                  <ul className="mt-2 ml-4 space-y-2 text-gray-500 list-disc">
                    <li>Identify your target market segments</li>
                    <li>Analyze competitor strengths and weaknesses</li>
                    <li>Discover market gaps and opportunities</li>
                  </ul>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="flex-shrink-0 w-6 h-6 text-green-500" />
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Value Proposition</h3>
                  <p className="mt-2 text-gray-500">
                    Define your unique selling points and how you'll solve customer problems. We provide:
                  </p>
                  <ul className="mt-2 ml-4 space-y-2 text-gray-500 list-disc">
                    <li>Value proposition canvas templates</li>
                    <li>Customer pain point analysis tools</li>
                    <li>Competitive differentiation frameworks</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Validation Strategy */}
          <section>
            <div className="flex items-center gap-2">
              <LineChart className="w-8 h-8 text-indigo-600" />
              <h2 className="text-3xl font-bold text-gray-900">2. Validation Strategy</h2>
            </div>
            <div className="mt-6 space-y-4">
              <div className="flex items-start">
                <CheckCircle2 className="flex-shrink-0 w-6 h-6 text-green-500" />
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Fake Door Testing</h3>
                  <p className="mt-2 text-gray-500">
                    Test market demand without building the full product. Our platform enables:
                  </p>
                  <ul className="mt-2 ml-4 space-y-2 text-gray-500 list-disc">
                    <li>Quick landing page creation</li>
                    <li>A/B testing different value propositions</li>
                    <li>Conversion tracking and analytics</li>
                  </ul>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="flex-shrink-0 w-6 h-6 text-green-500" />
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Customer Discovery</h3>
                  <p className="mt-2 text-gray-500">
                    Engage with potential customers to validate assumptions. We help you:
                  </p>
                  <ul className="mt-2 ml-4 space-y-2 text-gray-500 list-disc">
                    <li>Create customer interview scripts</li>
                    <li>Track and analyze feedback</li>
                    <li>Identify common patterns and insights</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Common Pitfalls */}
          <section>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-8 h-8 text-indigo-600" />
              <h2 className="text-3xl font-bold text-gray-900">3. Common Pitfalls to Avoid</h2>
            </div>
            <div className="mt-6 space-y-4">
              <div className="flex items-start">
                <CheckCircle2 className="flex-shrink-0 w-6 h-6 text-green-500" />
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Building Too Much</h3>
                  <p className="mt-2 text-gray-500">
                    Don't waste resources on unvalidated features. Our platform helps you:
                  </p>
                  <ul className="mt-2 ml-4 space-y-2 text-gray-500 list-disc">
                    <li>Focus on core value propositions</li>
                    <li>Test assumptions cheaply</li>
                    <li>Iterate based on real data</li>
                  </ul>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="flex-shrink-0 w-6 h-6 text-green-500" />
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Ignoring Data</h3>
                  <p className="mt-2 text-gray-500">
                    Make decisions based on evidence, not assumptions. We provide:
                  </p>
                  <ul className="mt-2 ml-4 space-y-2 text-gray-500 list-disc">
                    <li>Real-time analytics dashboard</li>
                    <li>Customer behavior insights</li>
                    <li>Data-driven decision frameworks</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Success Metrics */}
          <section>
            <div className="flex items-center gap-2">
              <Target className="w-8 h-8 text-indigo-600" />
              <h2 className="text-3xl font-bold text-gray-900">4. Measuring Success</h2>
            </div>
            <div className="mt-6 space-y-4">
              <div className="flex items-start">
                <CheckCircle2 className="flex-shrink-0 w-6 h-6 text-green-500" />
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Key Metrics</h3>
                  <p className="mt-2 text-gray-500">
                    Track the right metrics for your validation stage:
                  </p>
                  <ul className="mt-2 ml-4 space-y-2 text-gray-500 list-disc">
                    <li>Landing page conversion rates</li>
                    <li>Customer acquisition costs</li>
                    <li>User engagement metrics</li>
                    <li>Customer feedback scores</li>
                  </ul>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle2 className="flex-shrink-0 w-6 h-6 text-green-500" />
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">Decision Framework</h3>
                  <p className="mt-2 text-gray-500">
                    Know when to pivot or proceed. Our platform helps you:
                  </p>
                  <ul className="mt-2 ml-4 space-y-2 text-gray-500 list-disc">
                    <li>Set clear success criteria</li>
                    <li>Monitor progress in real-time</li>
                    <li>Make data-backed decisions</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Call to Action */}
          <div className="relative p-8 text-center rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600">
            <div className="absolute inset-0 opacity-10 bg-grid-white/10"></div>
            <div className="relative">
              <h2 className="text-3xl font-bold text-white">Ready to Launch Your Startup?</h2>
              <p className="mt-4 text-lg text-white/90">
                Join thousands of founders who've successfully validated their ideas with our platform.
              </p>
              <div className="flex justify-center mt-8 gap-4">
                <Button className="inline-flex items-center px-6 py-3 text-lg bg-white text-indigo-600 hover:bg-gray-100" asChild>
                  <a href="/signup">
                    Start Validating <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
                <Button className="inline-flex items-center px-6 py-3 text-lg border-2 border-white text-white hover:bg-white/10" variant="outline" asChild>
                  <a href="/pricing">
                    View Pricing <DollarSign className="w-5 h-5 ml-2" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreLaunchGuide; 